import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  LayoutDashboard, 
  Users, 
  Database, 
  Calendar,
  Server,
  CreditCard,
  UserCheck,
  LogOut,
  Home,
  ChevronRight,
  Settings,
  Shield,
  Crown,
  Activity,
  Brain,
  BrainCircuit,
  Ticket,
  Coins
} from 'lucide-react';
import { Button } from './ui/button';
import AdminNotifications from './AdminNotifications';

const AdminLayout = ({ children, title, subtitle }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { 
      path: '/admin', 
      label: 'Dashboard', 
      icon: LayoutDashboard,
      exact: true
    },
    { 
      path: '/admin/super-ia-master', 
      label: 'Sócrates Master', 
      icon: Crown,
      highlight: true
    },
    { 
      path: '/admin/users', 
      label: 'Usuarios', 
      icon: Users 
    },
    { 
      path: '/admin/user-activity', 
      label: 'Monitoreo', 
      icon: Activity,
      highlight: true
    },
    {
      path: '/admin/predictions',
      label: 'Predicciones',
      icon: Brain,
      highlight: true
    },
    {
      path: '/admin/analista',
      label: 'Analista Avanzado',
      icon: BrainCircuit,
      highlight: true
    },
    { 
      path: '/admin/lotteries', 
      label: 'Loterías', 
      icon: Database 
    },
    { 
      path: '/admin/results', 
      label: 'Resultados', 
      icon: Calendar 
    },
    { 
      path: '/admin/transactions', 
      label: 'Transacciones', 
      icon: CreditCard 
    },
    { 
      path: '/admin/affiliates', 
      label: 'Afiliados', 
      icon: UserCheck 
    },
    {
      path: '/admin/coupons',
      label: 'Cupones',
      icon: Ticket,
      highlight: true
    },
    {
      path: '/admin/credits',
      label: 'Créditos (Zelle)',
      icon: Coins,
      highlight: true
    },
    { 
      path: '/admin/system', 
      label: 'Sistema', 
      icon: Server 
    },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-gray-900 border-r border-gray-800 flex flex-col z-50">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-lg">Admin</h1>
              <p className="text-xs text-gray-500">NumeroMaestro</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`admin-sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                  active 
                    ? item.highlight 
                      ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 border border-yellow-500/30' 
                      : 'bg-amber-500/10 text-amber-400 border border-amber-500/20' 
                    : item.highlight
                      ? 'text-yellow-400 hover:bg-yellow-500/10 border border-transparent hover:border-yellow-500/20'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <Icon size={20} className={
                  active 
                    ? item.highlight ? 'text-yellow-400' : 'text-amber-400' 
                    : item.highlight ? 'text-yellow-500' : 'text-gray-500 group-hover:text-white'
                } />
                <span className="font-medium">{item.label}</span>
                {item.highlight && !active && (
                  <span className="ml-auto text-[10px] bg-yellow-500 text-black px-1.5 py-0.5 rounded font-bold">PRO</span>
                )}
                {active && <ChevronRight size={16} className={item.highlight ? "ml-auto text-yellow-400" : "ml-auto text-amber-400"} />}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:bg-gray-800 hover:text-white transition-all mb-2"
          >
            <Home size={20} />
            <span className="font-medium">Volver al sitio</span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all w-full"
            data-testid="admin-logout-btn"
          >
            <LogOut size={20} />
            <span className="font-medium">Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 ml-64">
        {/* Top Header */}
        <header className="h-16 bg-gray-900/50 backdrop-blur-xl border-b border-gray-800 flex items-center justify-between px-8 sticky top-0 z-40">
          <div>
            <h2 className="text-xl font-bold text-white">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
          
          <div className="flex items-center gap-4">
            <AdminNotifications />
            <button className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white transition-colors">
              <Settings size={20} />
            </button>
            <div className="flex items-center gap-3 pl-4 border-l border-gray-800">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <span className="font-bold text-white text-sm">
                  {user?.name?.charAt(0).toUpperCase() || 'A'}
                </span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
